import type { SessionResponse, ViewingAs } from '@aroo/shared';
import { createContext, useContext } from 'react';
import type { Access } from './access';

export interface ScopeChoice {
  id: string;
  label: string;
  kind: 'branch' | 'region' | 'all' | 'group';
  group?: boolean;
  /** A branch offered inside a region or division the person covers (indented under it). */
  within?: string;
  /** Indent level under `within`: 1 (default) for a branch or region, 2 for a branch in a region. */
  depth?: 1 | 2;
}

export interface SessionUser {
  id: string;
  firstName: string;
  name: string;
  initials: string;
  homeBranch: string;
  /** Home site code (for the branch calendar); null for head office / external people. */
  homeSiteCode: string | null;
  /** Job title, e.g. "Branch Manager". */
  roleTitle: string;
}

/**
 * Everything the shell and screens need to know about the signed-in person. Provided by
 * src/app/SessionProvider.tsx from `GET /auth/session`; the shape is what the shell consumes.
 */
export interface Session {
  signedIn: boolean;
  user: SessionUser;
  access: Access;
  /** Who the user's own holiday requests go to. */
  approverName: string;
  /** The account must set a new password before doing anything else. */
  mustChangePassword: boolean;
  scopes: readonly ScopeChoice[];
  scope: ScopeChoice;
  setScope: (id: string) => void;
  /** Set while viewing as someone else (D10): the shell is read-only and shows a banner. */
  viewingAs: ViewingAs | null;
  /** View as this person: the API switches the session, every screen reloads for them. */
  startViewAs: (personId: string) => Promise<void>;
  exitViewAs: () => Promise<void>;
  /** Called by the login page with the API's sign-in response. */
  signIn: (session: SessionResponse) => void;
  /** Ends the server session (best effort) and forgets everything locally. */
  signOut: () => Promise<void>;
  /** Re-reads the session from the API (after a password change, say). */
  refresh: () => Promise<void>;
}

export const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside a SessionProvider');
  return ctx;
}
